/**
 * TooltipElement web component
 * Handles mouse, keyboard, and touch events for tooltips.
 * Usage: Wrap the trigger and tooltip content in <tooltip-element>.
 */
class TooltipElement extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector('[data-tooltip-trigger]');
    this.tooltipContent = this.querySelector('[data-tooltip-content]');
    this.boundShow = this.show.bind(this);
    this.boundHide = this.hide.bind(this);
    this.boundToggle = this.toggle.bind(this);

    if (!this.trigger || !this.tooltipContent) return;

    // Mouse events
    this.trigger.addEventListener('mouseenter', this.boundShow);
    this.trigger.addEventListener('mouseleave', this.boundHide);

    // Keyboard events
    this.trigger.addEventListener('focus', this.boundShow);
    this.trigger.addEventListener('blur', this.boundHide);

    // Touch events
    this.trigger.addEventListener('touchstart', (e) => {
      e.preventDefault();
      this.boundToggle();
    });

    // Escape key (direct event listener)
    this.boundHandleEscape = (e) => {
      if (e.key === 'Escape' && this.isOpen()) this.hide();
    };
    document.addEventListener('keydown', this.boundHandleEscape);
  }

  disconnectedCallback() {
    if (!this.trigger) return;
    this.trigger.removeEventListener('mouseenter', this.boundShow);
    this.trigger.removeEventListener('mouseleave', this.boundHide);
    this.trigger.removeEventListener('focus', this.boundShow);
    this.trigger.removeEventListener('blur', this.boundHide);
    // Remove Escape key event listener
    document.removeEventListener('keydown', this.boundHandleEscape);
    // Touch event is not removed for anonymous function, but this is fine for simple tooltips
  }

  isOpen() {
    return this.trigger.getAttribute('aria-expanded') === 'true';
  }

  show() {
    this.tooltipContent.classList.remove('hidden');
    this.trigger.setAttribute('aria-expanded', 'true');
  }

  hide() {
    this.tooltipContent.classList.add('hidden');
    this.trigger.setAttribute('aria-expanded', 'false');
  }

  toggle() {
    this.isOpen() ? this.hide() : this.show();
  }
}

customElements.define('tooltip-element', TooltipElement);

export { TooltipElement };
